/**
 * Celebrity Grid Slider
 * Optimized with performance best practices
 */

class CelebrityGrid {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.slider = sectionEl.querySelector('[data-celebrity-slider]');
    this.splideInstance = null;
    this.isInitialized = false;

    if (!this.slider) {
      console.warn('[CelebrityGrid] Slider element not found');
      return;
    }

    this.init();
  }

  /**
   * Initialize the slider
   */
  init() {
    if (this.isInitialized || !window.Splide) {
      return;
    }

    this.setupSplide();
    this.bindNavigation();
    this.setupPagination();
    this.mountSlider();

    this.isInitialized = true;
    // console.log('[CelebrityGrid] Initialized successfully');
  }

  /**
   * Setup Splide configuration
   */
  setupSplide() {
    this.splideInstance = new Splide(this.slider, {
      type: 'loop',
      arrows: false,
      pagination: true,
      gap: '20px',
      perPage: 4,
      perMove: 1,
      speed: 600,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      autoplay: false,
      pauseOnHover: true,
      pauseOnFocus: true,
      lazyLoad: 'nearby',
      preloadPages: 1,
      clones: undefined,

      breakpoints: {
        1400: { perPage: 4, gap: '20px' },
        1200: { perPage: 4, gap: '16px' },
        992:  { perPage: 2.4, gap: '16px', clones: 6  },  // ⭐ shows 2 + peek
        768:  { perPage: 2.4, gap: '14px', clones: 6 },  // ⭐ shows 2 + peek
        480:  { perPage: 2.4, gap: '12px', clones: 6 },  // ⭐ shows 2 + peek
      },
    });
  }

  /**
   * Bind navigation arrows (desktop + mobile)
   */
  bindNavigation() {
    const prevButtons = this.section.querySelectorAll('[data-celebrity-prev]');
    const nextButtons = this.section.querySelectorAll('[data-celebrity-next]');

    prevButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.splideInstance.go('<');
      });
    });

    nextButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.splideInstance.go('>');
      });
    });
  }

  /**
   * Setup custom pagination with dot limiting
   */
  setupPagination() {
    this.splideInstance.on('pagination:mounted', (data) => {
      const paginationList = data.list;
      const mobileNav = this.section.querySelector('.cg__mobile-nav');

      if (!paginationList || !mobileNav) return;

      // Move pagination between mobile arrows
      const nextButton = mobileNav.querySelector('[data-celebrity-next]');
      mobileNav.insertBefore(paginationList, nextButton);

      // Get real slides (excluding clones in loop mode)
      const realSlides = this.slider.querySelectorAll(
        '.splide__list > .splide__slide:not(.splide__slide--clone)'
      );
      const slideCount = realSlides.length;

      // Limit dots to max 10 for performance
      const maxDots = 10;
      this.rebuildPagination(paginationList, slideCount, maxDots);
    });
  }

  /**
   * Rebuild pagination dots with limit
   */
  rebuildPagination(container, totalSlides, maxDots) {
    container.innerHTML = '';

    const createDot = (getIndex) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');

      btn.type = 'button';
      btn.className = 'splide__pagination__page';
      btn.setAttribute('aria-label', `Go to slide ${getIndex() + 1}`);

      btn.addEventListener('click', () => {
        this.splideInstance.go(getIndex());
      });

      li.appendChild(btn);
      container.appendChild(li);
    };

    if (totalSlides <= maxDots) {
      // Show all dots
      for (let i = 0; i < totalSlides; i++) {
        createDot(() => i);
      }
    } else {
      // Show limited dots (evenly distributed)
      for (let i = 0; i < maxDots; i++) {
        createDot(() => {
          return Math.round((i / (maxDots - 1)) * (totalSlides - 1));
        });
      }
    }

    // Sync active state
    this.syncPaginationActive(container, totalSlides, maxDots);
  }

  /**
   * Sync active dot state
   */
  syncPaginationActive(container, totalSlides, maxDots) {
    const updateActive = (currentIndex) => {
      const dots = container.querySelectorAll('.splide__pagination__page');
      const normalizedIndex = ((currentIndex % totalSlides) + totalSlides) % totalSlides;

      if (totalSlides <= maxDots) {
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === normalizedIndex);
        });
      } else {
        const activeDot = Math.round((normalizedIndex / (totalSlides - 1)) * (maxDots - 1));
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeDot);
        });
      }
    };

    // Initial state
    updateActive(this.splideInstance.index);

    // Update on move
    this.splideInstance.on('moved', (newIndex) => {
      updateActive(newIndex);
    });
  }

  /**
   * Mount the slider
   */
  mountSlider() {
    if (this.splideInstance) {
      this.splideInstance.mount();
    //   console.log('[CelebrityGrid] Slider mounted');
    }
  }

  /**
   * Destroy instance (for cleanup)
   */
  destroy() {
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.isInitialized = false;
    //   console.log('[CelebrityGrid] Slider destroyed');
    }
  }
}

/**
 * Initialize all celebrity grid sections
 */
function initCelebrityGrids() {
  const sections = document.querySelectorAll('.celebrity-slider');

  sections.forEach((section) => {
    // Prevent double initialization
    if (section.__celebrityGrid) return;

    section.__celebrityGrid = new CelebrityGrid(section);
  });
}

/**
 * Auto-initialize on DOM ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCelebrityGrids);
} else {
  initCelebrityGrids();
}

/**
 * Reinitialize on Shopify section events
 */
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.celebrity-slider');
  if (section) {
    new CelebrityGrid(section);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('.celebrity-slider');
  if (section && section.__celebrityGrid) {
    section.__celebrityGrid.destroy();
    delete section.__celebrityGrid;
  }
});

// Expose for manual control if needed
window.CelebrityGrid = CelebrityGrid;